import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { TableHeader } from 'components/table/table-content';
import { Filters } from 'elements/Filter';
import { FilterManager } from 'lib/tables/FilterManager';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Router } from 'aurelia-router';
import { AsyncErrorHandler } from 'lib/ui';
import { PenOfferService } from 'services/pen/pen-offer-service';
import { PenOfferListItemDto } from 'models/pen/PenOffers';
import { PubSub } from 'lib/event/PubSub';
import { CustomerService } from 'services/customer-service';
import { UserService } from 'services/user-service';
import { PenOfferStatusService } from 'services/pen/pen-offer-status-service';

@autoinject
export class PensOffersView {
  protected tableHeaders: TableHeader<PenOfferListItemDto>[] = [
    { key: 'Id', label: 'general.id', sortable: true },
    { key: 'QuoteNumber', label: 'offer.QuoteNumber', sortable: true },
    { key: 'CustomerName', label: 'general.customer', sortable: true },
    { key: 'SiteName', label: 'general.locality', sortable: true },
    { key: 'ResponsibleUserName', label: 'general.responsibleUser', sortable: true },
    { key: 'NumberOfPens', label: 'pens.numberOfPens', sortable: true },
    { key: 'DeliveryDate', label: 'offer.RequestedDeliveryDate', sortable: true },
    { key: 'OfferValidToDate', label: 'offer.ValidTo', sortable: true },
    { key: 'PenOfferStatus', label: 'general.status', sortable: false },
    { key: 'Created', label: 'general.created', sortable: true },
  ];

  protected penOffers: PenOfferListItemDto[] = [];
  protected exportPenOffersProgress: boolean = false;

  constructor(
    private t: I18N,
    protected fm: FilterManager,
    private eventAggregator: EventAggregator,
    private router: Router,
    private penOfferService: PenOfferService,
    private pubsub: PubSub,
    private userService: UserService,
    private customerService: CustomerService,
    private penOfferStatusService: PenOfferStatusService
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      this.getOffers();
    });
  }

  @AsyncErrorHandler
  async getCustomers() {
    const customres = await this.customerService.getAll();
    return customres.filter((x) => !x.IsDeleted);
  }

  @AsyncErrorHandler
  async getResponsibleUsers() {
    return await this.userService.getAll();
  }

  @AsyncErrorHandler
  async getStatuses() {
    const statuses = await this.penOfferStatusService.getAll();
    return statuses.filter((x) => !x.IsDeleted);
  }

  protected async attached() {
    this.fm.paginationTotalRecords = 0;
    const customers = await this.getCustomers();
    const responsibleUsers = await this.getResponsibleUsers();
    const statuses = await this.getStatuses();
    this.fm.init({
      context: 'pens-offers',
      storageKey: 'pens-offers',
      filters: {
        customerIds: {
          type: 'checkbox',
          label: this.t.tr('general.customer'),
          name: 'customerIds',
          query: 'customerIds',
          originalData: customers,
          options: customers.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
        },
        circumference: {
          name: 'circumference',
          label: this.t.tr('pens.circumference'),
          type: 'range',
        },
        responsibleuserIds: {
          type: 'checkbox',
          label: this.t.tr('general.responsibleUser'),
          name: 'responsibleuserIds',
          query: 'responsibleuserIds',
          originalData: responsibleUsers,
          options: responsibleUsers.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
        },
        statusIds: {
          type: 'checkbox',
          label: this.t.tr('general.status'),
          name: 'statusIds',
          query: 'statusIds',
          originalData: statuses,
          options: statuses.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
        },
      } satisfies Filters,
      defaultFilters: {
        customerIds: true,
        responsibleuserIds: true,
        statusIds: true,
        circumference: true,
      },
    });

    await this.getOffers();
  }

  @AsyncErrorHandler
  async getOffers() {
    const { data, headers } = await this.penOfferService.getOffers(this.fm.queryFilter);
    const record = headers.get('x-total-count');
    this.fm.paginationTotalRecords = record ? +record : 0;
    this.penOffers = data;
  }

  @AsyncErrorHandler
  async export() {
    this.exportPenOffersProgress = true;
    await this.penOfferService.getOffersExport(this.fm.queryFilter);
    this.exportPenOffersProgress = false;
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  newOffer() {
    this.eventAggregator.publish('PensCreateOfferDialogOpen', true);
  }

  protected openOffer(penOfferId: number, penId: number) {
    this.router.navigate(`pen-offer/${penOfferId}/pen/${penId}`);
  }
}
